// ============================================================================
// CLEO'S SECRET — ResultsReveal
// File: src/pages/ResultsReveal.jsx
//
// The patient's findings revealed across six sequential screens:
//   Stage 0 — Threshold (welcome, OBS ceremonial)
//   Stage 1 — Pattern (named pattern + GP narrative, on PAP)
//   Stage 2 — Score (baseline vs today, on PAP)
//   Stage 3 — Numbers (markers grouped by category, on PAP)
//   Stage 4 — Questions (AI-drafted question list, on PAP)
//   Stage 5 — Complete (ceremonial close, OBS)
//
// Pattern: PATIENT-FACING EDITORIAL with a state machine. The OBS-on-OBS
// bookends (Threshold + Complete) frame the cream interior screens
// (Pattern/Score/Numbers/Questions) like a ceremony — the obsidian gives
// the moment its weight; the cream lets the patient read at their own pace.
//
// Already on the May 2026 design system; this pass:
//   • Removes unused T + AnkhSm imports
//   • Inline Cinzel font strings replaced with CINZEL constant
//   • 4-6px decorative left bars unified at 3px GO
//   • "Saving..." → real ellipsis
//   • Threshold + Complete ceremonial typography refined for editorial weight
//   • Sentence case throughout
//
// API endpoints preserved:
//   • /api/get-reveal-state           — loads all four stages of data
//   • /api/advance-reveal             — saves last stage completed
//   • /api/draft-consultation-questions — generates AI starter list
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  P, FF, FS,
  AnkhMed, GeoDivider, BtnPrimary, BtnGhost, Spinner,
} from '../lib/design-tokens';
import Ankh from '../components/Ankh';

// ───────── Helpers ─────────
function formatDate(s) {
  if (!s) return '';
  return new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function getFlagColor(flag) {
  if (flag === 'optimal') return P.GREEN;
  if (flag === 'borderline') return P.AMBER;
  if (flag === 'high' || flag === 'low') return P.RED;
  return P.BR2;
}
function getFlagBgColor(flag) {
  if (flag === 'optimal') return P.GREEN_BG;
  if (flag === 'borderline') return P.AMBER_BG;
  if (flag === 'high' || flag === 'low') return P.RED_BG;
  return 'rgba(201,169,97,0.08)';
}
function formatFlag(flag) {
  if (flag === 'optimal') return 'Optimal';
  if (flag === 'normal') return 'In range';
  if (flag === 'borderline') return 'Borderline';
  if (flag === 'high') return 'Above range';
  if (flag === 'low') return 'Below range';
  return flag || '';
}
function formatCategory(cat) {
  var labels = {
    thyroid: 'Thyroid',
    sex_hormones: 'Sex hormones',
    metabolic: 'Metabolic',
    iron_studies: 'Iron studies',
    vitamins_minerals: 'Vitamins and minerals',
    inflammation: 'Inflammation',
    liver_kidney: 'Liver and kidney',
    full_blood_count: 'Full blood count',
    other: 'Other',
  };
  return labels[cat] || cat;
}

// ═════════════════════════════════════════════════════════════════════════
// ResultsReveal — state machine entry point
// ═════════════════════════════════════════════════════════════════════════
export default function ResultsReveal(props) {
  var onNavigate = props.onNavigate;
  var email = props.email;
  var firstName = props.firstName;

  var dataState = useState(null);
  var data = dataState[0];
  var setData = dataState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  var stageState = useState(0);
  var stage = stageState[0];
  var setStage = stageState[1];

  var emailWaitState = useState(0);
  var emailWaitMs = emailWaitState[0];
  var setEmailWaitMs = emailWaitState[1];

  useEffect(function() {
    if (!email) {
      if (emailWaitMs >= 3000) {
        setError('You need to be logged in to view your findings.');
        setLoading(false);
        return;
      }
      var timer = setTimeout(function() { setEmailWaitMs(emailWaitMs + 500); }, 500);
      return function() { clearTimeout(timer); };
    }
    loadReveal();
  }, [email, emailWaitMs]);

  function loadReveal() {
    setLoading(true);
    setError(null);
    fetch('/api/get-reveal-state', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email }),
    })
      .then(function(res) {
        if (!res.ok) return res.json().then(function(e) { throw new Error(e.error || 'Could not load findings'); });
        return res.json();
      })
      .then(function(result) {
        setData(result);
        var lastStage = result.revealState && result.revealState.lastStageCompleted;
        if (lastStage && lastStage > 0) setStage(Math.min(lastStage, 4));
        setLoading(false);
      })
      .catch(function(err) { setError(err.message); setLoading(false); });
  }

  function advanceToStage(targetStage) {
    setStage(targetStage);
    if (data && data.revealState && targetStage >= 1 && targetStage <= 4) {
      fetch('/api/advance-reveal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email, revealStateId: data.revealState.id, stage: targetStage }),
      }).catch(function() {});
    }
    window.scrollTo(0, 0);
  }

  // ────────── LOADING STATE (OBS to match Threshold) ──────────
  if (loading) {
    return (
      <div style={{
        background: P.OBS,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Spinner color={P.GO2} size={48} />
      </div>
    );
  }

  // ────────── ERROR / NOT-RELEASED STATE ──────────
  if (error || !data) {
    return (
      <div style={{
        background: P.OBS,
        minHeight: '100vh',
        padding: 'clamp(80px, 12vw, 140px) clamp(24px, 3vw, 40px)',
        textAlign: 'center',
        fontFamily: FF,
      }}>
        <div style={{ maxWidth: 620, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Ankh responsive="opener" color={P.GO} />
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.95vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.34em',
            textTransform: 'uppercase',
            color: P.GO2,
            margin: 'clamp(32px, 3.6vw, 44px) 0 22px',
          }}>Not yet ready</div>

          <h1 style={{
            fontFamily: FF,
            fontSize: 'clamp(34px, 4.6vw, 56px)',
            fontWeight: 400,
            color: P.IVY,
            lineHeight: 1.1,
            letterSpacing: '-0.014em',
            margin: '0 0 clamp(24px, 2.8vw, 36px)',
          }}>
            Your findings have not yet been <span style={{ fontStyle: 'italic', color: P.GO2 }}>released.</span>
          </h1>

          <div style={{
            width: 'clamp(120px, 12vw, 160px)',
            height: 1,
            background: P.GO,
            margin: '0 auto clamp(28px, 3vw, 36px)',
            opacity: 0.85,
          }} />

          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(17px, 1.7vw, 21px)',
            color: 'rgba(240,232,212,0.85)',
            lineHeight: 1.75,
            maxWidth: 540,
            margin: '0 auto clamp(40px, 4.6vw, 56px)',
          }}>
            {error || 'Your GP is still reviewing. You will receive an email when they are ready.'}
          </p>

          <BtnGhost onDark onClick={function() { if (onNavigate) onNavigate('portal'); }}>
            Back to portal
          </BtnGhost>
        </div>
      </div>
    );
  }

  var reportPdfUrl = data.revealState && data.revealState.reportPdfUrl;

  // ────────── Stage routing ──────────
  if (stage === 0) return <ThresholdScreen firstName={firstName} onBegin={function() { advanceToStage(1); }} />;
  if (stage === 1) return <PatternScreen data={data} onNext={function() { advanceToStage(2); }} onExit={function() { if (onNavigate) onNavigate('portal'); }} />;
  if (stage === 2) return <ScoreScreen data={data} onPrev={function() { advanceToStage(1); }} onNext={function() { advanceToStage(3); }} />;
  if (stage === 3) return <NumbersScreen data={data} onPrev={function() { advanceToStage(2); }} onNext={function() { advanceToStage(4); }} />;
  if (stage === 4) return <QuestionsScreen data={data} email={email} onPrev={function() { advanceToStage(3); }} onComplete={function() { setStage(5); }} onReload={loadReveal} onNavigate={onNavigate} />;
  if (stage === 5) return <CompleteScreen onNavigate={onNavigate} onReopen={function() { setStage(1); }} reportPdfUrl={reportPdfUrl} />;
  return null;
}

// ═════════════════════════════════════════════════════════════════════════
// STAGE 0 — THRESHOLD (ceremonial OBS welcome)
// ═════════════════════════════════════════════════════════════════════════
function ThresholdScreen({ firstName, onBegin }) {
  return (
    <div style={{
      background: P.OBS,
      minHeight: '100vh',
      padding: 'clamp(40px, 5vw, 64px) clamp(24px, 3vw, 40px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: FF,
    }}>
      <div style={{ maxWidth: 780, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Ankh responsive="opener" color={P.GO} />

        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(11px, 0.95vw, 13px)',
          fontWeight: 700,
          letterSpacing: '0.34em',
          textTransform: 'uppercase',
          color: P.GO2,
          margin: 'clamp(40px, 4.6vw, 52px) 0 26px',
        }}>Your findings are ready when you are</div>

        <h1 style={{
          fontFamily: FF,
          fontSize: 'clamp(46px, 6.8vw, 80px)',
          fontWeight: 400,
          color: P.IVY,
          lineHeight: 1.04,
          letterSpacing: '-0.015em',
          margin: '0 0 clamp(36px, 4vw, 48px)',
        }}>
          {firstName ? firstName + ',' : 'Welcome,'}
          <br />
          <span style={{
            fontStyle: 'italic',
            color: P.GO2,
            fontWeight: 400,
          }}>take your time.</span>
        </h1>

        <div style={{
          width: 'clamp(120px, 12vw, 160px)',
          height: 1,
          background: P.GO,
          opacity: 0.85,
          margin: '0 auto clamp(40px, 4.6vw, 56px)',
        }} />

        <p style={{
          fontFamily: FF,
          fontSize: 'clamp(18px, 2.1vw, 24px)',
          fontStyle: 'italic',
          color: 'rgba(240,232,212,0.95)',
          lineHeight: 1.55,
          maxWidth: 640,
          margin: '0 auto clamp(20px, 2.4vw, 28px)',
        }}>What you are about to see is the most detailed picture of your physiology you have ever had.</p>

        <p style={{
          fontFamily: FF,
          fontSize: 'clamp(16px, 1.7vw, 19px)',
          color: 'rgba(240,232,212,0.78)',
          lineHeight: 1.75,
          maxWidth: 580,
          margin: '0 auto clamp(48px, 5.6vw, 64px)',
        }}>Your GP has written this for you personally. There is no rush. You can return to it as many times as you want.</p>

        <button
          onClick={onBegin}
          style={{
            fontFamily: FS,
            fontSize: 'clamp(12px, 1.1vw, 14px)',
            fontWeight: 700,
            letterSpacing: '0.36em',
            textTransform: 'uppercase',
            padding: '22px 56px',
            background: P.GO,
            color: P.OBS,
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
          }}
          onMouseEnter={function(e) { e.currentTarget.style.background = P.GO2; }}
          onMouseLeave={function(e) { e.currentTarget.style.background = P.GO; }}>
          Open my findings
        </button>

        <p style={{
          fontFamily: FF,
          fontSize: 'clamp(13px, 1.2vw, 15px)',
          fontStyle: 'italic',
          color: 'rgba(240,232,212,0.6)',
          lineHeight: 1.5,
          margin: 'clamp(48px, 5.6vw, 64px) auto 0',
        }}>Cleopatra studied the science of her own body. So can you.</p>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// STAGE INDICATOR (used on stages 1-4)
// ═════════════════════════════════════════════════════════════════════════
function StageIndicator({ currentStage, onDark }) {
  var labels = ['Pattern', 'Score', 'Numbers', 'Questions'];
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 'clamp(24px, 3vw, 44px)',
      padding: 'clamp(36px, 4.4vw, 56px) 24px 0',
      flexWrap: 'wrap',
    }}>
      {labels.map(function(label, i) {
        var stageNum = i + 1;
        var isActive = stageNum === currentStage;
        var isComplete = stageNum < currentStage;
        return (
          <div key={i} style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}>
            <div style={{
              width: 10,
              height: 10,
              background: isActive || isComplete
                ? (onDark ? P.GO2 : P.GO)
                : (onDark ? 'rgba(224,198,133,0.3)' : P.SAND3),
              transform: 'rotate(45deg)',
              transition: 'background 0.3s',
            }} />
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(11px, 0.95vw, 13px)',
              fontWeight: 700,
              letterSpacing: '0.34em',
              textTransform: 'uppercase',
              color: isActive
                ? (onDark ? P.GO2 : P.BR2)
                : isComplete
                ? (onDark ? 'rgba(224,198,133,0.72)' : P.BR)
                : (onDark ? 'rgba(224,198,133,0.42)' : P.TL),
            }}>{label}</div>
          </div>
        );
      })}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// STAGE 1 — PATTERN
// ═════════════════════════════════════════════════════════════════════════
function PatternScreen({ data, onNext, onExit }) {
  var p = data.stage1_pattern;
  return (
    <div style={{ background: P.PAP, minHeight: '100vh', fontFamily: FF }}>
      <StageIndicator currentStage={1} />

      <div style={{
        maxWidth: 840,
        margin: '0 auto',
        padding: 'clamp(48px, 6vw, 72px) clamp(24px, 3vw, 40px) clamp(64px, 8vw, 112px)',
      }}>
        {/* Editorial header */}
        <div style={{ textAlign: 'center', marginBottom: 'clamp(40px, 5vw, 56px)' }}>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.95vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.34em',
            textTransform: 'uppercase',
            color: P.BR2,
            marginBottom: 22,
          }}>Your pattern, named</div>

          <h1 style={{
            fontFamily: FF,
            fontSize: 'clamp(42px, 6vw, 72px)',
            fontWeight: 400,
            color: P.INK,
            lineHeight: 1.05,
            letterSpacing: '-0.015em',
            margin: 0,
          }}>{p.patternName || 'Your clinical pattern'}</h1>

          {p.shortDescriptor && (
            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(20px, 2.3vw, 28px)',
              fontStyle: 'italic',
              color: P.BR2,
              lineHeight: 1.5,
              maxWidth: 680,
              margin: 'clamp(24px, 2.8vw, 32px) auto 0',
            }}>{p.shortDescriptor}</p>
          )}

          {p.secondaryPatternName && (
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(11px, 0.95vw, 13px)',
              fontWeight: 700,
              letterSpacing: '0.34em',
              textTransform: 'uppercase',
              color: P.BR2,
              marginTop: 'clamp(24px, 2.8vw, 32px)',
            }}>With a contributing {p.secondaryPatternName}</div>
          )}
        </div>

        <GeoDivider marginV={56} />

        {/* Narrative paper */}
        <div style={{
          background: P.SAND,
          border: '1px solid ' + P.SAND3,
          borderLeft: '3px solid ' + P.GO,
          padding: 'clamp(36px, 4.8vw, 64px)',
        }}>
          <NarrativeText text={p.narrativeText} />
        </div>

        {/* Footer nav */}
        <div style={{
          marginTop: 'clamp(48px, 6vw, 72px)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}>
          <BtnGhost onClick={onExit}>Save for later</BtnGhost>
          <BtnPrimary onClick={onNext}>Continue to your score</BtnPrimary>
        </div>
      </div>
    </div>
  );
}

function NarrativeText({ text }) {
  if (!text) {
    return (
      <p style={{
        fontFamily: FF,
        fontSize: 'clamp(17px, 1.8vw, 20px)',
        fontStyle: 'italic',
        color: P.TF,
        lineHeight: 1.7,
      }}>Your GP has not yet written your narrative.</p>
    );
  }
  var paragraphs = text.split(/\n\n+/).map(function(s) { return s.trim(); }).filter(function(s) { return s.length > 0; });
  return (
    <div>
      {paragraphs.map(function(para, i) {
        return (
          <p key={i} style={{
            fontFamily: FF,
            fontSize: 'clamp(17px, 1.85vw, 21px)',
            color: P.TX,
            lineHeight: 1.85,
            textAlign: 'justify',
            WebkitHyphens: 'auto',
            msHyphens: 'auto',
            hyphens: 'auto',
            margin: '0 0 26px',
          }}>{para}</p>
        );
      })}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// STAGE 2 — SCORE
// ═════════════════════════════════════════════════════════════════════════
function ScoreScreen({ data, onPrev, onNext }) {
  var s = data.stage2_score;
  var hasBaseline = s.baselineScore !== null && s.baselineScore !== undefined;
  var hasCurrent = s.currentScore !== null && s.currentScore !== undefined;
  var delta = s.delta;

  return (
    <div style={{ background: P.PAP, minHeight: '100vh', fontFamily: FF }}>
      <StageIndicator currentStage={2} />

      <div style={{
        maxWidth: 860,
        margin: '0 auto',
        padding: 'clamp(48px, 6vw, 72px) clamp(24px, 3vw, 40px) clamp(64px, 8vw, 112px)',
      }}>
        {/* Editorial header */}
        <div style={{ textAlign: 'center', marginBottom: 'clamp(44px, 5.4vw, 60px)' }}>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.95vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.34em',
            textTransform: 'uppercase',
            color: P.BR2,
            marginBottom: 22,
          }}>Your Cleo Score</div>

          <h1 style={{
            fontFamily: FF,
            fontSize: 'clamp(34px, 4.4vw, 56px)',
            fontWeight: 400,
            color: P.INK,
            lineHeight: 1.08,
            letterSpacing: '-0.014em',
            margin: 0,
          }}>
            Where you started, where you <span style={{ fontStyle: 'italic', color: P.BR2 }}>are now.</span>
          </h1>
        </div>

        {/* Score panel */}
        <div className="cs-score-reveal" style={{
          background: P.SAND,
          border: '1px solid ' + P.SAND3,
          padding: 'clamp(40px, 5.4vw, 72px)',
          display: 'grid',
          gridTemplateColumns: hasBaseline && hasCurrent ? '1fr auto 1fr' : '1fr',
          gap: 'clamp(28px, 3.6vw, 44px)',
          alignItems: 'center',
        }}>
          {hasBaseline && (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(11px, 0.95vw, 13px)',
                fontWeight: 700,
                letterSpacing: '0.34em',
                textTransform: 'uppercase',
                color: P.BR2,
                marginBottom: 18,
              }}>Baseline</div>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(72px, 10vw, 124px)',
                fontWeight: 400,
                color: P.TL,
                lineHeight: 1,
                letterSpacing: '-0.02em',
                marginBottom: 14,
              }}>{s.baselineScore}</div>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(13px, 1.15vw, 14px)',
                fontStyle: 'italic',
                color: P.BR2,
              }}>{s.baselineCalculatedAt ? formatDate(s.baselineCalculatedAt) : 'Initial assessment'}</div>
            </div>
          )}

          {hasBaseline && hasCurrent && (
            <div style={{ textAlign: 'center' }}>
              <svg width="52" height="32" viewBox="0 0 32 20" fill="none">
                <path d="M2 10 H28 M22 4 L28 10 L22 16" stroke={P.BR} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {delta !== null && delta !== undefined && (
                <div style={{
                  fontFamily: FF,
                  fontSize: 'clamp(11px, 0.95vw, 13px)',
                  fontWeight: 700,
                  letterSpacing: '0.34em',
                  textTransform: 'uppercase',
                  color: delta > 0 ? P.GREEN : delta < 0 ? P.AMBER : P.TL,
                  marginTop: 20,
                }}>{delta > 0 ? '+' : ''}{delta}</div>
              )}
            </div>
          )}

          {hasCurrent && (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(11px, 0.95vw, 13px)',
                fontWeight: 700,
                letterSpacing: '0.34em',
                textTransform: 'uppercase',
                color: P.BR2,
                marginBottom: 18,
              }}>Today</div>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(72px, 10vw, 124px)',
                fontWeight: 400,
                color: P.INK,
                lineHeight: 1,
                letterSpacing: '-0.02em',
                marginBottom: 14,
              }}>{s.currentScore}</div>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(13px, 1.15vw, 14px)',
                fontStyle: 'italic',
                color: P.BR2,
              }}>{s.currentCalculatedAt ? formatDate(s.currentCalculatedAt) : 'With pathology'}</div>
            </div>
          )}

          {!hasBaseline && !hasCurrent && (
            <div style={{ textAlign: 'center', padding: 'clamp(40px, 4.8vw, 56px) 0' }}>
              <p style={{
                fontFamily: FF,
                fontSize: 'clamp(18px, 1.8vw, 22px)',
                fontStyle: 'italic',
                color: P.TF,
                lineHeight: 1.55,
                margin: 0,
              }}>Your Cleo Score is being calculated.</p>
            </div>
          )}
        </div>

        {/* Explanation callout */}
        {(hasBaseline || hasCurrent) && (
          <div style={{
            marginTop: 'clamp(36px, 4.4vw, 52px)',
            padding: 'clamp(26px, 3vw, 36px) clamp(28px, 3.4vw, 40px)',
            background: 'rgba(232,221,196,0.5)',
            borderLeft: '3px solid ' + P.GO,
          }}>
            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(16px, 1.7vw, 19px)',
              color: P.TX,
              lineHeight: 1.85,
              textAlign: 'justify',
              WebkitHyphens: 'auto',
              msHyphens: 'auto',
              hyphens: 'auto',
              margin: 0,
            }}>
              Your Cleo Score is one number that summarises your overall wellbeing across four dimensions. It is most useful as a <em style={{ color: P.BR, fontFamily: FF, fontStyle: 'italic', fontSize: '1.05em' }}>direction of travel</em>. What matters more than today's number is how it moves over time.
            </p>
          </div>
        )}

        {/* Footer nav */}
        <div style={{
          marginTop: 'clamp(48px, 6vw, 72px)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}>
          <BtnGhost onClick={onPrev}>&larr; Pattern</BtnGhost>
          <BtnPrimary onClick={onNext}>See your numbers</BtnPrimary>
        </div>
      </div>

      <style>{`
        @media (max-width: 560px) {
          .cs-score-reveal { grid-template-columns: 1fr !important; }
          .cs-score-reveal svg { transform: rotate(90deg); }
        }
      `}</style>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// STAGE 3 — NUMBERS
// ═════════════════════════════════════════════════════════════════════════
function NumbersScreen({ data, onPrev, onNext }) {
  var n = data.stage3_numbers;
  var markers = n.markers || [];
  var grouped = {};
  markers.forEach(function(m) {
    var cat = m.marker_category || 'other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(m);
  });

  return (
    <div style={{ background: P.PAP, minHeight: '100vh', fontFamily: FF }}>
      <StageIndicator currentStage={3} />

      <div style={{
        maxWidth: 880,
        margin: '0 auto',
        padding: 'clamp(48px, 6vw, 72px) clamp(24px, 3vw, 40px) clamp(64px, 8vw, 112px)',
      }}>
        {/* Editorial header */}
        <div style={{ textAlign: 'center', marginBottom: 'clamp(40px, 5vw, 56px)' }}>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.95vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.34em',
            textTransform: 'uppercase',
            color: P.BR2,
            marginBottom: 22,
          }}>Your numbers</div>

          <h1 style={{
            fontFamily: FF,
            fontSize: 'clamp(34px, 4.4vw, 56px)',
            fontWeight: 400,
            color: P.INK,
            lineHeight: 1.08,
            letterSpacing: '-0.014em',
            margin: 0,
          }}>
            The evidence behind your <span style={{ fontStyle: 'italic', color: P.BR2 }}>pattern.</span>
          </h1>

          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(16px, 1.7vw, 20px)',
            color: P.TL,
            lineHeight: 1.7,
            maxWidth: 660,
            margin: 'clamp(20px, 2.4vw, 28px) auto 0',
          }}>Most of your numbers will be unremarkable. That is information too. Some will be the evidence behind your pattern.</p>
        </div>

        {/* Empty state */}
        {markers.length === 0 && (
          <div style={{
            padding: 'clamp(48px, 6vw, 72px)',
            textAlign: 'center',
            background: 'rgba(232,221,196,0.4)',
            border: '1px dashed ' + P.SAND3,
          }}>
            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(17px, 1.8vw, 20px)',
              fontStyle: 'italic',
              color: P.BR2,
              lineHeight: 1.55,
              margin: 0,
            }}>Your detailed markers are still being processed.</p>
          </div>
        )}

        {/* Overall picture (from patient_summary if present) */}
        {n.interpretation && n.interpretation.ai_content && n.interpretation.ai_content.patient_summary && n.interpretation.ai_content.patient_summary.overall_picture && (
          <div style={{
            background: P.SAND,
            border: '1px solid ' + P.SAND3,
            borderLeft: '3px solid ' + P.GO,
            padding: 'clamp(28px, 3.6vw, 48px)',
            marginBottom: 'clamp(32px, 4vw, 44px)',
          }}>
            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(18px, 2vw, 23px)',
              fontStyle: 'italic',
              color: P.TX,
              lineHeight: 1.7,
              margin: 0,
              textAlign: 'justify',
              WebkitHyphens: 'auto',
              msHyphens: 'auto',
              hyphens: 'auto',
            }}>{n.interpretation.ai_content.patient_summary.overall_picture}</p>
          </div>
        )}

        {/* Grouped markers */}
        {Object.keys(grouped).map(function(cat) {
          return (
            <div key={cat} style={{ marginBottom: 'clamp(32px, 4vw, 44px)' }}>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(12px, 1vw, 14px)',
                fontWeight: 700,
                letterSpacing: '0.34em',
                textTransform: 'uppercase',
                color: P.BR2,
                marginBottom: 22,
                paddingBottom: 12,
                borderBottom: '1px solid ' + P.SAND3,
              }}>{formatCategory(cat)}</div>

              {grouped[cat].map(function(m, i) {
                return <MarkerRow key={i} marker={m} />;
              })}
            </div>
          );
        })}

        {/* Footer nav */}
        <div style={{
          marginTop: 'clamp(48px, 6vw, 72px)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}>
          <BtnGhost onClick={onPrev}>&larr; Score</BtnGhost>
          <BtnPrimary onClick={onNext}>Your questions</BtnPrimary>
        </div>
      </div>
    </div>
  );
}

function MarkerRow({ marker }) {
  var flagColor = getFlagColor(marker.flag);
  var flagBgColor = getFlagBgColor(marker.flag);
  return (
    <div style={{
      padding: 'clamp(20px, 2.4vw, 28px)',
      background: P.PAP,
      border: '1px solid ' + P.SAND3,
      borderLeft: '3px solid ' + flagColor,
      marginBottom: 12,
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 16,
        marginBottom: 12,
        flexWrap: 'wrap',
      }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.95vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.34em',
            textTransform: 'uppercase',
            color: P.BR2,
            marginBottom: 10,
          }}>{marker.marker_name}</div>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(26px, 3vw, 36px)',
            fontWeight: 400,
            color: P.INK,
            lineHeight: 1,
            letterSpacing: '-0.012em',
          }}>
            {marker.value}{' '}
            <span style={{
              fontSize: 'clamp(14px, 1.4vw, 17px)',
              color: P.TF,
              fontStyle: 'italic',
            }}>{marker.unit}</span>
          </div>
          {marker.reference_range && (
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(12px, 1.05vw, 13px)',
              fontStyle: 'italic',
              color: P.TF,
              marginTop: 10,
            }}>Reference: {marker.reference_range}</div>
          )}
        </div>

        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(11px, 0.95vw, 13px)',
          fontWeight: 700,
          letterSpacing: '0.34em',
          textTransform: 'uppercase',
          color: flagColor,
          background: flagBgColor,
          border: '1px solid ' + flagColor,
          padding: '6px 14px',
          whiteSpace: 'nowrap',
        }}>{formatFlag(marker.flag)}</div>
      </div>

      {marker.ai_interpretation && (
        <div style={{
          marginTop: 16,
          paddingTop: 16,
          borderTop: '1px dashed ' + P.SAND3,
          fontFamily: FF,
          fontSize: 'clamp(15px, 1.5vw, 17px)',
          fontStyle: 'italic',
          color: P.TL,
          lineHeight: 1.75,
        }}>{marker.ai_interpretation}</div>
      )}

      {marker.optimal_range_note && (
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(12px, 1.05vw, 13px)',
          fontStyle: 'italic',
          color: P.TF,
          marginTop: 12,
        }}>{marker.optimal_range_note}</div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// STAGE 4 — QUESTIONS
// ═════════════════════════════════════════════════════════════════════════
function QuestionsScreen({ data, email, onPrev, onComplete, onReload, onNavigate }) {
  var q = data.stage4_questions;
  var draftedQuestions = q.aiDraftedQuestions || [];
  var finalQuestions = q.finalQuestions || [];

  var loadingState = useState(false);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  function generateDraft() {
    setLoading(true);
    fetch('/api/draft-consultation-questions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email }),
    })
      .then(function(res) { return res.json(); })
      .then(function() { setLoading(false); if (onReload) onReload(); })
      .catch(function() { setLoading(false); });
  }

  var displayList = finalQuestions.length > 0 ? finalQuestions : draftedQuestions;

  return (
    <div style={{ background: P.PAP, minHeight: '100vh', fontFamily: FF }}>
      <StageIndicator currentStage={4} />

      <div style={{
        maxWidth: 840,
        margin: '0 auto',
        padding: 'clamp(48px, 6vw, 72px) clamp(24px, 3vw, 40px) clamp(64px, 8vw, 112px)',
      }}>
        {/* Editorial header */}
        <div style={{ textAlign: 'center', marginBottom: 'clamp(40px, 5vw, 56px)' }}>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.95vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.34em',
            textTransform: 'uppercase',
            color: P.BR2,
            marginBottom: 22,
          }}>Your questions</div>

          <h1 style={{
            fontFamily: FF,
            fontSize: 'clamp(34px, 4.4vw, 56px)',
            fontWeight: 400,
            color: P.INK,
            lineHeight: 1.08,
            letterSpacing: '-0.014em',
            margin: 0,
          }}>
            What you might want to <span style={{ fontStyle: 'italic', color: P.BR2 }}>ask.</span>
          </h1>

          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(16px, 1.7vw, 20px)',
            color: P.TL,
            lineHeight: 1.7,
            maxWidth: 660,
            margin: 'clamp(20px, 2.4vw, 28px) auto 0',
          }}>
            Your GP has drafted a starting list. <em style={{ color: P.BR2, fontFamily: FF, fontStyle: 'italic', fontSize: '1.05em' }}>Edit them, add your own, delete what does not matter.</em> The list you bring is yours.
          </p>
        </div>

        {/* Empty state */}
        {displayList.length === 0 && !loading && (
          <div style={{
            padding: 'clamp(48px, 6vw, 72px)',
            textAlign: 'center',
            background: 'rgba(232,221,196,0.4)',
            border: '1px dashed ' + P.SAND3,
          }}>
            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(18px, 1.8vw, 22px)',
              fontStyle: 'italic',
              color: P.BR2,
              lineHeight: 1.5,
              maxWidth: 480,
              margin: '0 auto clamp(28px, 3vw, 40px)',
            }}>No questions yet. Generate a starting list based on your pattern.</p>
            <BtnPrimary onClick={generateDraft}>Generate question list</BtnPrimary>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div style={{
            padding: 'clamp(48px, 6vw, 72px)',
            textAlign: 'center',
          }}>
            <Spinner color={P.GO} size={48} />
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(11px, 0.95vw, 13px)',
              fontWeight: 700,
              letterSpacing: '0.34em',
              textTransform: 'uppercase',
              color: P.BR2,
              marginTop: 28,
              marginBottom: 14,
            }}>Drafting</div>
            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(17px, 1.7vw, 21px)',
              fontStyle: 'italic',
              color: P.TX,
              margin: 0,
            }}>Cleo is drafting your questions.</p>
          </div>
        )}

        {/* Question list */}
        {displayList.length > 0 && !loading && (
          <div>
            {displayList.map(function(item, i) {
              return (
                <div key={item.id || i} style={{
                  padding: 'clamp(22px, 2.6vw, 30px)',
                  background: P.PAP,
                  border: '1px solid ' + P.SAND3,
                  borderLeft: '3px solid ' + P.GO,
                  marginBottom: 12,
                }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '40px 1fr',
                    gap: 'clamp(16px, 1.8vw, 22px)',
                    alignItems: 'flex-start',
                  }}>
                    <div style={{
                      fontFamily: FF,
                      fontSize: 'clamp(11px, 0.95vw, 13px)',
                      fontWeight: 700,
                      letterSpacing: '0.34em',
                      color: P.BR2,
                      paddingTop: 6,
                    }}>{(i + 1).toString().padStart(2, '0')}</div>

                    <div>
                      <p style={{
                        fontFamily: FF,
                        fontSize: 'clamp(17px, 1.9vw, 22px)',
                        fontStyle: 'italic',
                        color: P.TX,
                        lineHeight: 1.55,
                        margin: 0,
                      }}>{item.text}</p>

                      {item.category && (
                        <div style={{
                          fontFamily: FF,
                          fontSize: 'clamp(11px, 0.95vw, 13px)',
                          fontWeight: 700,
                          letterSpacing: '0.34em',
                          textTransform: 'uppercase',
                          color: P.BR2,
                          marginTop: 12,
                        }}>{item.category}</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            <div style={{
              marginTop: 'clamp(28px, 3vw, 40px)',
              textAlign: 'center',
            }}>
              <BtnGhost onClick={function() { if (onNavigate) onNavigate('consultation-questions'); }}>
                Edit my questions
              </BtnGhost>
            </div>
          </div>
        )}

        {/* Footer nav */}
        <div style={{
          marginTop: 'clamp(48px, 6vw, 72px)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}>
          <BtnGhost onClick={onPrev}>&larr; Numbers</BtnGhost>
          <BtnPrimary onClick={onComplete}>Complete</BtnPrimary>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// STAGE 5 — COMPLETE (ceremonial OBS close)
// ═════════════════════════════════════════════════════════════════════════
function CompleteScreen({ onNavigate, onReopen, reportPdfUrl }) {
  return (
    <div style={{
      background: P.OBS,
      minHeight: '100vh',
      padding: 'clamp(40px, 5vw, 64px) clamp(24px, 3vw, 40px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: FF,
    }}>
      <div style={{ maxWidth: 740, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <AnkhMed color={P.GO2} opacity={0.9} />
        </div>

        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(11px, 0.95vw, 13px)',
          fontWeight: 700,
          letterSpacing: '0.34em',
          textTransform: 'uppercase',
          color: P.GO2,
          margin: 'clamp(40px, 4.6vw, 52px) 0 26px',
        }}>You have read it through</div>

        <h1 style={{
          fontFamily: FF,
          fontSize: 'clamp(38px, 5.4vw, 68px)',
          fontWeight: 400,
          color: P.IVY,
          lineHeight: 1.06,
          letterSpacing: '-0.015em',
          margin: '0 0 clamp(32px, 3.6vw, 44px)',
        }}>
          You are ready for your <span style={{ fontStyle: 'italic', color: P.GO2 }}>consultation.</span>
        </h1>

        <div style={{
          width: 'clamp(120px, 12vw, 160px)',
          height: 1,
          background: P.GO,
          opacity: 0.85,
          margin: '0 auto clamp(36px, 4vw, 48px)',
        }} />

        <p style={{
          fontFamily: FF,
          fontSize: 'clamp(17px, 1.9vw, 22px)',
          color: 'rgba(240,232,212,0.88)',
          lineHeight: 1.7,
          maxWidth: 620,
          margin: '0 auto clamp(48px, 5.6vw, 64px)',
        }}>Return to this report as many times as you want before we meet. Your edited question list is what your GP will work from.</p>

        {/* Stacked actions */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          alignItems: 'center',
        }}>
          <BtnPrimary onClick={function() { if (onNavigate) onNavigate('consultation-questions'); }}>
            Refine my questions
          </BtnPrimary>
          {reportPdfUrl && (
            <BtnGhost onDark onClick={function() { window.open(reportPdfUrl, '_blank'); }}>
              Download my report (PDF)
            </BtnGhost>
          )}
          <BtnGhost onDark onClick={onReopen}>Re-read my findings</BtnGhost>
        </div>

        {/* Subtle back link */}
        <div style={{ marginTop: 'clamp(48px, 5.4vw, 64px)' }}>
          <button
            onClick={function() { if (onNavigate) onNavigate('portal'); }}
            style={{
              fontFamily: FS,
              fontSize: 'clamp(10px, 0.85vw, 12px)',
              fontWeight: 600,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              color: 'rgba(240,232,212,0.62)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              transition: 'color 0.2s',
            }}
            onMouseEnter={function(e) { e.currentTarget.style.color = P.GO2; }}
            onMouseLeave={function(e) { e.currentTarget.style.color = 'rgba(240,232,212,0.62)'; }}>
            Back to portal
          </button>
        </div>
      </div>
    </div>
  );
}
